<script lang="ts">
  import { slide } from "svelte/transition"
  import { ChevronDown, ChevronRight } from "lucide-svelte"

  import Questions from "../Questions/Index.svelte"

  let props = $props()
  let { questions, response } = props
  let pdf_mode = $state(props.pdf_mode ?? false)

  let expand_details = $state(false)

  let alignment_questions = Object.entries(questions).filter(
    ([step, q]) => q.category === "decision"
  )

  function answer_letter(i: number): string {
    if (i == 1) {
      return "A"
    }
    if (i == 2) {
      return "B"
    }
    if (i == 3) {
      return "C"
    }
    if (i == 4) {
      return "D"
    }
  }
</script>

<div class="rounded-lg border-gray-200 lg:mt-8" class:border={!pdf_mode}>
  <h3
    class="text-lg lg:text-2xl font-semibold sm:font-bold text-black w-full py-3 px-5 bg-blue-900 text-white"
  >
    {#if response.score.decision < 25}
      2. DECISION-MAKING: You’re the Only One Leading
    {:else if response.score.decision >= 25 && response.score.decision < 50}
      2. DECISION-MAKING: You’re the System - and That’s the Problem
    {:else if response.score.decision >= 50 && response.score.decision < 80}
      2. DECISION-MAKING: You’re the System - and That’s the Problem
    {:else if response.score.decision >= 80 && response.score.decision < 100}
      2. HIDDEN OVERRELIANCE
    {:else if response.score.decision === 100}
      2. UNSEEN DEPENDENCY
    {/if}
  </h3>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 p-4 lg:p-6">
    {#if response.score.decision < 25}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          Your team can’t move without you. And while you might be proud of your
          leadership instincts - this is no longer a strength. It’s a liability.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          The business depends on you - but readiness means it can function
          without you.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          What this looks like:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Delegation always comes back
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Progress stalls unless you’re personally involved
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            No one feels confident making big calls
          </li>
        </ul>
      </div>
    {:else if response.score.decision >= 25 && response.score.decision < 50}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          You’re smart. Capable. Probably faster than most.
        </p>
        <p class="mt-4 lg:mt-6 text-base lg:text-lg/8 text-gray-600">
          But if every major decision runs through you, your company will always
          move at the speed of your availability.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          You don’t need more hours. You need a system that replicates
          decision-making at every level.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Warning signs:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You constantly review, edit, or fix others’ work
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Delegation ends up back on your plate
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Progress slows when you’re in meetings, on break, or pulled away
          </li>
        </ul>
      </div>
    {:else if response.score.decision >= 50 && response.score.decision < 80}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          If your quiz answers pointed to “I’ll just do it myself” moments,
          you’re not alone. But it’s not sustainable.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          Fast Attack Organizations don’t rely on a single hero. They rely on
          readiness systems that replicate leadership at every level.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Without a readiness framework, here’s what happens:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You become the bottleneck
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Delegation turns into micromanagement
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Innovation flatlines while you’re buried in ops
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Every break, vacation, or crisis feels like a risk
          </li>
        </ul>
      </div>
    {:else if response.score.decision >= 80 && response.score.decision < 100}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          Your team performs well. But if you disappeared for 30 days, would
          they lead - or just maintain?
        </p>
        <p class="mt-2 lg:mt-6 text-base lg:text-lg/8 text-gray-600">
          Here’s the challenge:
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8 ml-4">
          Fast Attack Leaders aren’t central nodes. They’re system builders.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          A high score can hide the subtle signs of over-functioning:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You review most decisions, even if briefly
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Team members defer when the stakes are high
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You think, “They’re good... but not there yet”
          </li>
        </ul>
      </div>
    {:else if response.score.decision == 100}
      <div>
        <p class="text-base lg:text-xl/8 font-bold text-gray-700">
          When you perform at this level, it’s easy to become the invisible
          center of gravity. Even if your team executes, they may still orbit
          around your clarity and confidence.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          Fast Attack Leaders don’t just perform. They replicate capability at
          every level.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lgsm:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-xl/8 font-bold text-gray-700">
          If you stepped away for 90 days - not 3 - could they adapt, lead, and
          evolve without losing momentum?
        </p>
      </div>
    {/if}
  </div>

  <div>
    {#if pdf_mode}
      <p class="text-xl font-bold text-gray-900 mt-2 lg:mt-6">
        Detailed question analysis
      </p>

      <div class="py-2 grid grid-cols-1 gap-y-2">
        {#each alignment_questions as [step, question]}
          {@const response_answer = response.response_answers.find(
            (ra) => ra.step == step
          )}
          <div class="p-4" style="break-after: page;">
            <p class="text-xl text-center text-blue-900 font-bold">
              Question {step}
            </p>
            <p class="text-lg text-gray-700 font-bold mt-4">
              {question.question}
            </p>

            <div class="mx-auto max-w-xl mt-6">
              {#each Object.entries(question.answers) as [i, answer]}
                <p
                  class="text-sm text-gray-600 py-1 px-2 flex"
                  class:font-bold={response_answer.answer == i}
                  class:bg-blue-100={response_answer.answer == i}
                >
                  {answer_letter(i)})
                  {answer}
                </p>
              {/each}
            </div>
            <p class="text-sm text-gray-700 font-semibold italic mt-6">
              Analysis of your answer:
            </p>
            <div class="col-span-2 mt-2">
              <Questions question={step} answer={response_answer.answer} />
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <button
        class="w-full text-base lg:text-lg px-4 py-3 border-t border-gray-200 bg-gray-50 hover:bg-gray-100 hover:text-blue-700 text-left font-bold flex items-center justify-start"
        class:bg-gray-100={expand_details}
        class:text-blue-700={expand_details}
        onclick={() => (expand_details = !expand_details)}
      >
        {#if expand_details}
          <ChevronDown class="size-5 lg:size-6" />
        {:else}
          <ChevronRight class="size-5 lg:size-6" />
        {/if}
        View Detailed Question Analysis</button
      >

      {#if expand_details}
        <div
          transition:slide={{ duration: 600 }}
          class="px-2 sm:px-4 py-2 sm:py-4 grid grid-cols-1 gap-y-4 sm:gap-y-6"
        >
          {#each alignment_questions as [step, question]}
            {@const response_answer = response.response_answers.find(
              (ra) => ra.step == step
            )}
            <div
              class="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-gray-50 border border-gray-100 rounded p-2 sm:p-4"
            >
              <div>
                <p
                  class="text-base lg:text-lg/8 lg:text-xl/8 text-gray-700 font-bold"
                >
                  {step}. {question.question}
                </p>
              </div>
              <div>
                {#each Object.entries(question.answers) as [i, answer]}
                  <p
                    class="text-sm lg:text-base/8 text-gray-600 py-1 px-2"
                    class:font-bold={response_answer.answer == i}
                    class:bg-blue-100={response_answer.answer == i}
                  >
                    {answer_letter(i)})
                    {answer}
                  </p>
                {/each}
              </div>

              <div class="col-span-1 lg:col-span-2">
                <Questions question={step} answer={response_answer.answer} />
              </div>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</div>
